import React, { Suspense, lazy } from 'react';
import { Header } from './components/ToxicDetector/Header';
import { HeroSection } from './components/ToxicDetector/HeroSection';
import { TrustSection } from './components/ToxicDetector/TrustSection';
import { Footer } from './components/ToxicDetector/Footer';
import { MetaTags } from './components/SEO/MetaTags';
import { ProStatusListener } from './components/ProStatusListener';
import { LazySection } from './components/LazySection';
// Lazy-load every below-the-fold section. Each gets its own
// LazySection wrapper which adds IntersectionObserver + its own Suspense
// boundary, so chunks are only requested as the user scrolls near them.
const SocialProofSection = lazy(() => import('./components/ToxicDetector/SocialProofSection').then(m => ({
  default: m.SocialProofSection
})));
const CredentialsImageSection = lazy(() => import('./components/ToxicDetector/CredentialsImageSection').then(m => ({
  default: m.CredentialsImageSection
})));
const HiddenToxicitySection = lazy(() => import('./components/ToxicDetector/HiddenToxicitySection').then(m => ({
  default: m.HiddenToxicitySection
})));
const DetectionImageSection = lazy(() => import('./components/ToxicDetector/DetectionImageSection').then(m => ({
  default: m.DetectionImageSection
})));
const ToxicityAuditSection = lazy(() => import('./components/ToxicDetector/ToxicityAuditSection').then(m => ({
  default: m.ToxicityAuditSection
})));
const WarningImageSection = lazy(() => import('./components/ToxicDetector/WarningImageSection').then(m => ({
  default: m.WarningImageSection
})));
const FeaturesSection = lazy(() => import('./components/ToxicDetector/FeaturesSection').then(m => ({
  default: m.FeaturesSection
})));
const HowItWorksSection = lazy(() => import('./components/ToxicDetector/HowItWorksSection').then(m => ({
  default: m.HowItWorksSection
})));
const GuaranteesSection = lazy(() => import('./components/ToxicDetector/GuaranteesSection').then(m => ({
  default: m.GuaranteesSection
})));
const IntroImageSection = lazy(() => import('./components/ToxicDetector/IntroImageSection').then(m => ({
  default: m.IntroImageSection
})));
const ToolsSection = lazy(() => import('./components/ToxicDetector/ToolsSection').then(m => ({
  default: m.ToolsSection
})));
const ComparisonSection = lazy(() => import('./components/ToxicDetector/ComparisonSection').then(m => ({
  default: m.ComparisonSection
})));
const PricingSection = lazy(() => import('./components/ToxicDetector/PricingSection').then(m => ({
  default: m.PricingSection
})));
const FAQSection = lazy(() => import('./components/ToxicDetector/FAQSection').then(m => ({
  default: m.FAQSection
})));
const JourneyImageSection = lazy(() => import('./components/ToxicDetector/JourneyImageSection').then(m => ({
  default: m.JourneyImageSection
})));
const CTASection = lazy(() => import('./components/ToxicDetector/CTASection').then(m => ({
  default: m.CTASection
})));
const SEONavigationSection = lazy(() => import('./components/ToxicDetector/SEONavigationSection').then(m => ({
  default: m.SEONavigationSection
})));
// Lazy-load non-critical UI that appears after user interaction or scroll
const PromoBanner = lazy(() => import('./components/Notifications/PromoBanner').then(m => ({
  default: m.PromoBanner
})));
const FloatingActionBar = lazy(() => import('./components/ToxicDetector/FloatingActionBar').then(m => ({
  default: m.FloatingActionBar
})));
export function App() {
  return <>
      <ProStatusListener data-id="element-19" />
      <MetaTags data-id="element-20" />
      <Suspense fallback={null} data-id="element-21">
        <PromoBanner data-id="element-22" />
      </Suspense>
      <div className="flex flex-col w-full min-h-screen bg-gradient-to-b from-stone-50 to-white" data-id="element-23">
        <Header data-id="element-24" />
        <main data-id="element-25">
          {/* Above the fold — eager loaded so first paint is instant. */}
          <HeroSection data-id="element-26" />
          <TrustSection data-id="element-27" />

          {/* Below the fold — each section streams in independently
           via IntersectionObserver + its own Suspense boundary. */}
          <LazySection minHeight={520} data-id="element-28">
            <SocialProofSection data-id="element-29" />
          </LazySection>
          <LazySection minHeight={420} data-id="element-30">
            <CredentialsImageSection data-id="element-31" />
          </LazySection>
          <LazySection minHeight={600} data-id="element-32">
            <HiddenToxicitySection data-id="element-33" />
          </LazySection>
          <LazySection minHeight={420} data-id="element-34">
            <DetectionImageSection data-id="element-35" />
          </LazySection>
          <LazySection minHeight={600} data-id="element-36">
            <ToxicityAuditSection data-id="element-37" />
          </LazySection>
          <LazySection minHeight={420} data-id="element-38">
            <WarningImageSection data-id="element-39" />
          </LazySection>
          <LazySection minHeight={700} data-id="element-40">
            <FeaturesSection data-id="element-41" />
          </LazySection>
          <LazySection minHeight={700} data-id="element-42">
            <HowItWorksSection data-id="element-43" />
          </LazySection>
          <LazySection minHeight={700} data-id="element-44">
            <GuaranteesSection data-id="element-45" />
          </LazySection>
          <LazySection minHeight={420} data-id="element-46">
            <IntroImageSection data-id="element-47" />
          </LazySection>
          <LazySection minHeight={700} data-id="element-48">
            <ToolsSection data-id="element-49" />
          </LazySection>
          <LazySection minHeight={700} data-id="element-50">
            <ComparisonSection data-id="element-51" />
          </LazySection>
          <LazySection minHeight={700} data-id="element-52">
            <PricingSection data-id="element-53" />
          </LazySection>
          <LazySection minHeight={600} data-id="element-54">
            <FAQSection data-id="element-55" />
          </LazySection>
          <LazySection minHeight={420} data-id="element-56">
            <JourneyImageSection data-id="element-57" />
          </LazySection>
          <LazySection minHeight={500} data-id="element-58">
            <CTASection data-id="element-59" />
          </LazySection>
          <LazySection minHeight={300} data-id="element-60">
            <SEONavigationSection data-id="element-61" />
          </LazySection>
        </main>
        <Footer data-id="element-62" />
      </div>
      <Suspense fallback={null} data-id="element-63">
        <FloatingActionBar data-id="element-64" />
      </Suspense>
    </>;
}